import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  toast,
} from "@devhop/ui";
import {
  Briefcase,
  ExternalLink,
  FileText,
  Home,
  IdCard,
  MapPin,
  Save,
  ShieldCheck,
  User,
} from "lucide-react";
import * as RHF from "react-hook-form";
import type {
  EmployeeExtendedProfileDTO,
  UpsertEmployeeProfileDTO,
} from "@/modules/hr/employee-profile/domain/types";
import { config } from "@/shared/lib/config";
import { PresignedFileUpload } from "@/shared/ui/PresignedFileUpload";
import { useUpsertEmployeeProfileMutation } from "../api/queries";

type FormValues = Omit<UpsertEmployeeProfileDTO, "userId" | "actorId">;

type Props = {
  userId: string;
  data: EmployeeExtendedProfileDTO | null | undefined;
  canEditSensitive?: boolean;
};

const GENDER_OPTIONS = [
  { value: "male", label: "ຊາຍ" },
  { value: "female", label: "ຍິງ" },
  { value: "other", label: "ອື່ນໆ" },
];

const MARITAL_OPTIONS = [
  { value: "single", label: "ໂສດ" },
  { value: "married", label: "ແຕ່ງງານ" },
  { value: "divorced", label: "ຢ່າຮ້າງ" },
  { value: "widowed", label: "ໝ້າຍ" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "ຍັງເຮັດວຽກ" },
  { value: "probation", label: "ທົດລອງງານ" },
  { value: "probation_failed", label: "ບໍ່ຜ່ານທົດລອງ" },
  { value: "resigned", label: "ລາອອກ" },
  { value: "terminated", label: "ຖືກເລີກຈ້າງ" },
];

function buildDefaultValues(
  data: EmployeeExtendedProfileDTO | null | undefined,
): FormValues {
  return {
    employeeCode: data?.employeeCode ?? undefined,
    branch: data?.branch ?? undefined,
    nameLao: data?.nameLao ?? data?.userName ?? undefined,
    nameEn: data?.nameEn ?? undefined,
    gender: data?.gender ?? undefined,
    dateOfBirth: data?.dateOfBirth ?? undefined,
    nationality: data?.nationality ?? undefined,
    ethnicity: data?.ethnicity ?? undefined,
    religion: data?.religion ?? undefined,
    bloodType: data?.bloodType ?? undefined,
    maritalStatus: data?.maritalStatus ?? undefined,
    birthVillage: data?.birthVillage ?? undefined,
    birthDistrict: data?.birthDistrict ?? undefined,
    birthProvince: data?.birthProvince ?? undefined,
    currentVillage: data?.currentVillage ?? undefined,
    currentDistrict: data?.currentDistrict ?? undefined,
    currentProvince: data?.currentProvince ?? undefined,
    nationalId: data?.nationalId ?? undefined,
    bankAccountName: data?.bankAccountName ?? undefined,
    bankAccountNumber: data?.bankAccountNumber ?? undefined,
    status: data?.status ?? "active",
    startDate: data?.startDate ?? undefined,
    probationEndDate: data?.probationEndDate ?? undefined,
    resignationDate: data?.resignationDate ?? undefined,
    documentFileKey: data?.documentFileKey ?? undefined,
    documentFilesUrl: data?.documentFilesUrl ?? undefined,
    notes: data?.notes ?? undefined,
    phoneNumber: data?.phoneNumber ?? undefined,
  };
}

function SectionCard({
  icon,
  title,
  badge,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-semibold text-muted-foreground text-sm uppercase tracking-wide">
          {icon}
          {title}
          {badge}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

export function EmployeePersonalInfoForm({
  userId,
  data,
  canEditSensitive,
}: Props) {
  const mutation = useUpsertEmployeeProfileMutation(userId);

  const methods = RHF.useForm<FormValues>({
    defaultValues: buildDefaultValues(data),
    values: data ? buildDefaultValues(data) : undefined,
  });

  function onSubmit(values: FormValues) {
    mutation.mutate(values, {
      onSuccess: () => toast.success("ບັນທຶກຂໍ້ມູນພະນັກງານສຳເລັດ"),
      onError: (err) => toast.error(err?.message ?? "ບັນທຶກບໍ່ສຳເລັດ"),
    });
  }

  return (
    <FormRoot<FormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      {/* Section 1: ລະຫັດ & ສາຂາ */}
      <SectionCard icon={<IdCard className="size-4" />} title="ລະຫັດ ແລະ ສາຂາ">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormInput
            name="employeeCode"
            label="ລະຫັດພະນັກງານ"
            placeholder="FD001"
          />
          <FormInput name="branch" label="ສາຂາ" placeholder="HQ / PTB / DDB" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInput name="nameLao" label="ຊື່ ແລະ ນາມສະກຸນ (ລາວ)" />
          <FormInput name="nameEn" label="Full Name (English)" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInput
            name="phoneNumber"
            label="ເບີໂທລະສັບ"
            type="tel"
            placeholder="020 xxxx xxxx"
          />
        </div>
      </SectionCard>

      {/* Section 2: ຂໍ້ມູນສ່ວນຕົວ */}
      <SectionCard icon={<User className="size-4" />} title="ຂໍ້ມູນສ່ວນຕົວ">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <FormSelect
            name="gender"
            label="ເພດ"
            options={GENDER_OPTIONS}
            placeholder="ເລືອກ"
          />
          <FormInput name="dateOfBirth" label="ວັນເກີດ" type="date" />
          <FormInput
            name="bloodType"
            label="ກຸ່ມເລືອດ"
            placeholder="A / B / AB / O"
          />
          <FormSelect
            name="maritalStatus"
            label="ສະຖານະສົມລົດ"
            options={MARITAL_OPTIONS}
            placeholder="ເລືອກ"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormInput name="nationality" label="ສັນຊາດ" placeholder="ລາວ" />
          <FormInput name="ethnicity" label="ຊົນເຜົ່າ" placeholder="ລາວລຸ່ມ" />
          <FormInput name="religion" label="ສາສະໜາ" placeholder="ພຸດ" />
        </div>
      </SectionCard>

      {/* Section 3 & 4: ທີ່ຢູ່ — birth + current side by side */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard icon={<MapPin className="size-4" />} title="ທີ່ຢູ່ເກີດ">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormInput name="birthVillage" label="ບ້ານ" />
            <FormInput name="birthDistrict" label="ເມືອງ" />
            <FormInput name="birthProvince" label="ແຂວງ" />
          </div>
        </SectionCard>
        <SectionCard icon={<Home className="size-4" />} title="ທີ່ຢູ່ປັດຈຸບັນ">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormInput name="currentVillage" label="ບ້ານ" />
            <FormInput name="currentDistrict" label="ເມືອງ" />
            <FormInput name="currentProvince" label="ແຂວງ" />
          </div>
        </SectionCard>
      </div>

      {/* Section 5: ເອກກະສານ & ບັນຊີທະນາຄານ (sensitive) */}
      {canEditSensitive && (
        <SectionCard
          icon={<ShieldCheck className="size-4" />}
          title="ເອກກະສານ ແລະ ບັນຊີທະນາຄານ"
          badge={
            <Badge variant="outline" className="ml-auto font-normal text-xs">
              Sensitive
            </Badge>
          }
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormInput name="nationalId" label="ເລກບັດ / ສຳມະໂນຄົວ" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormInput name="bankAccountName" label="ຊື່ບັນຊີທະນາຄານ (ທະນາຄານ)" />
            <FormInput
              name="bankAccountNumber"
              label="ເລກບັນຊີທະນາຄານ (ທະນາຄານ)"
            />
          </div>
        </SectionCard>
      )}

      {/* Section 6: ສະຖານະການເຮັດວຽກ */}
      <SectionCard
        icon={<Briefcase className="size-4" />}
        title="ສະຖານະການເຮັດວຽກ"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormSelect
            name="status"
            label="ສະຖານະ"
            options={STATUS_OPTIONS}
            placeholder="ເລືອກ"
          />
          <FormInput name="startDate" label="ວັນທີເລີ່ມງານ" type="date" />
          <FormInput name="probationEndDate" label="ວັນສິ້ນສຸດທົດລອງ" type="date" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormInput name="resignationDate" label="ວັນທີລາອອກ" type="date" />
        </div>
      </SectionCard>

      {/* Section 7: ໄຟລ໌ & ໝາຍເຫດ */}
      <SectionCard
        icon={<FileText className="size-4" />}
        title="ໄຟລ໌ ແລະ ໝາຍເຫດ"
      >
        {/* File upload to object store */}
        <div className="space-y-2">
          <p className="font-medium text-sm">ໄຟລ໌ເອກກະສານພະນັກງານ</p>
          {methods.watch("documentFileKey") && (
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
              <FileText className="size-4 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate font-mono text-muted-foreground text-xs">
                {methods.watch("documentFileKey")}
              </span>
              <a
                href={`${config.apiUrl}/files/${methods.watch("documentFileKey")}`}
                target="_blank"
                rel="noreferrer"
                className="flex shrink-0 items-center gap-1 text-primary text-xs hover:underline"
              >
                <ExternalLink className="size-3" />
                ເປີດ
              </a>
            </div>
          )}
          <PresignedFileUpload
            keyPrefix="uploads/employee-docs"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            buttonLabel={
              methods.watch("documentFileKey") ? "ອັບໂຫຼດໄຟລ໌ໃໝ່" : "ອັບໂຫຼດໄຟລ໌"
            }
            hint="ຮອງຮັບ PDF, Word, Excel, ຮູບພາບ"
            onSuccess={(key) => {
              methods.setValue("documentFileKey", key);
              methods.setValue("documentFilesUrl", undefined);
            }}
            onError={(e) => toast.error(e.message)}
          />
        </div>

        {/* Fallback: external URL */}
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">
            ຫຼື ລິ້ງ URL ພາຍນອກ (Google Drive, OneDrive)
          </p>
          <FormInput
            name="documentFilesUrl"
            placeholder="https://drive.google.com/..."
          />
        </div>

        <FormTextarea
          name="notes"
          label="ໝາຍເຫດ"
          rows={3}
          placeholder="ໝາຍເຫດເພີ່ມເຕີມ..."
        />
      </SectionCard>

      {/* Submit */}
      <div className="flex justify-end pb-2">
        <Button
          type="submit"
          disabled={mutation.isPending}
          className="gap-2 px-6"
        >
          <Save className="size-4" />
          {mutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຂໍ້ມູນ"}
        </Button>
      </div>
    </FormRoot>
  );
}
